import { alpha, Popper, Tooltip, useTheme } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faPause, faPlay, IconDefinition } from "@fortawesome/sharp-solid-svg-icons";
import { FC, useRef, useState } from "react";
import { Box, Button } from "../../platform-style";
import { setStatusStyle, StatusColors, Statuses } from "./statusUtils";

// TODO: This component needs a complete rework once legacy support is no longer required

export type StatusButtonIcon = "play" | "pause" | "check";

export interface RichTooltipData {
  type: string;
  component?: React.ReactNode;
}

export interface StatusButtonProps {
  tooltip?: string;
  tooltipPlacement?: "top" | "bottom" | "left" | "right";
  richTooltipData?: RichTooltipData;
  label: string;
  onClick?: () => void;
  startIcon?: StatusButtonIcon | IconDefinition;
  endIcon?: StatusButtonIcon | IconDefinition;
  status: Statuses;
  disabled?: boolean;
  size?: "small" | "medium" | "large";
}

export const StatusButton: FC<StatusButtonProps> = ({
  status,
  tooltip,
  tooltipPlacement,
  richTooltipData,
  onClick,
  label,
  startIcon,
  endIcon,
  disabled,
  size,
}) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);

  function getColors(color: Statuses): StatusColors {
    let returnColor: StatusColors = {};
    return setStatusStyle(color, returnColor, theme);
  }

  const handleClick = () => {
    if (richTooltipData?.component) {
      setOpen((prevOpen) => !prevOpen);
    }
    if (onClick) {
      onClick();
    }
  };

  return (
    <StatusButtonTooltip
      tooltip={tooltip}
      richTooltipData={richTooltipData}
      tooltipPlacement={tooltipPlacement}
      open={open}
      setOpen={setOpen}
    >
      <Button
        focusRipple={false}
        onClick={handleClick}
        aria-label={label}
        disabled={disabled}
        sx={{
          borderRadius: size === "large" ? "20px" : "8px",
          color: theme.palette.secondary.contrastText,
          background: `linear-gradient(0deg, ${alpha(
            theme.palette.secondary.main,
            0.56
          )} 0%, ${alpha(theme.palette.secondary.main, 0.56)} 100%), ${
            theme.palette.background.paper
          }`,
          m: "2px",
          typography: "labelLarge",
          textTransform: "none",
          height: size === "large" ? "36px" : "32px",
          "&:hover": {
            background: `linear-gradient(0deg, ${alpha(
              theme.palette.secondary.contrastText,
              0.08
            )} 0%, ${alpha(theme.palette.secondary.contrastText, 0.08)} 100%), ${
              theme.palette.secondary.main
            }`,
            color: theme.palette.secondary.contrastText,
            boxShadow: "none",
          },
          "&:focus": {
            background: `linear-gradient(0deg, ${alpha(
              theme.palette.secondary.contrastText,
              0.12
            )} 0%, ${alpha(theme.palette.secondary.contrastText, 0.12)} 100%), ${
              theme.palette.secondary.main
            }`,
            color: theme.palette.secondary.contrastText,
            outlineStyle: "solid",
            outlineWidth: "2px",
            outlineOffset: "2px",
          },
          "&:active": {
            background: `linear-gradient(0deg, ${alpha(
              theme.palette.secondary.contrastText,
              0.16
            )} 0%, ${alpha(theme.palette.secondary.contrastText, 0.16)} 100%), ${
              theme.palette.secondary.main
            }`,
            color: theme.palette.secondary.contrastText,
            boxShadow: "none",
          },
          "&:disabled": {
            backgroundColor: `linear-gradient(0deg, ${alpha(
              theme.palette.primary.main,
              0.12
            )} 0%, ${alpha(theme.palette.primary.main, 0.12)} 100%), ${
              theme.palette.background.paper
            }`, // 12% opacity
            color: theme.palette.text.disabled,
            // boxShadow: "none",
          },
          letterSpacing: "0px",
          ...getColors(status ?? "default"),
        }}
        startIcon={
          startIcon && (
            <Box sx={{ ml: 1, mt: "2px" }}>
              <FontAwesomeIcon
                size={"sm"}
                icon={getIconElement(startIcon)}
                color={getColors(status ?? "default").color}
              />
            </Box>
          )
        }
        endIcon={
          endIcon && (
            <Box sx={{ mr: 1, mt: "2px" }}>
              <FontAwesomeIcon size={"sm"} icon={getIconElement(endIcon)} color={"black"} />
            </Box>
          )
        }
      >
        <Box component={"span"} sx={{ ml: startIcon ? 0 : 1, mr: endIcon ? 0 : 1, mt: "1px" }}>
          {label}
        </Box>
      </Button>
    </StatusButtonTooltip>
  );
};

interface StatusButtonTooltip {
  richTooltipData?: RichTooltipData;
  children: React.ReactNode;
  tooltip?: string;
  tooltipPlacement?: "top" | "bottom" | "left" | "right";
  open: boolean;
  setOpen: (open: boolean) => void;
}

const StatusButtonTooltip = ({
  richTooltipData,
  children,
  tooltip,
  tooltipPlacement,
  open,
  setOpen,
}: StatusButtonTooltip) => {
  const theme = useTheme();
  const anchorRef = useRef<HTMLDivElement | null>(null);

  // Handle mouse enter to show tooltip
  const handleMouseEnter = () => {
    if (richTooltipData?.component) {
      setOpen(true);
    }
  };

  // Handle mouse leave to hide tooltip
  const handleMouseLeave = () => {
    if (richTooltipData?.component) {
      setOpen(false);
    }
  };

  if (richTooltipData?.component) {
    return (
      <Box
        component="div"
        ref={anchorRef}
        style={{
          position: "relative",
          display: "inline-block",
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
        <Popper
          open={open}
          anchorEl={anchorRef.current}
          placement={tooltipPlacement === "top" ? "top" : "bottom-start"}
          disablePortal={false}
          sx={{
            zIndex: 2,
          }}
          modifiers={[
            {
              name: "offset",
              options: {
                offset: [0, 8],
              },
            },
          ]}
        >
          <Box
            sx={{
              background: theme.palette.background.paper,
              width: "max-content",
              border: `1px solid`,
              borderColor: "divider",
              color: theme.palette.text.primary,
              fontFamily: theme.typography.fontFamily,
              borderRadius: "8px",
              padding: "8px",
            }}
          >
            {richTooltipData.component}
          </Box>
        </Popper>
      </Box>
    );
  }
  return (
    <Tooltip title={tooltip ?? ""} placement={tooltipPlacement ?? "top"} arrow>
      <Box component={"span"}>{children}</Box>
    </Tooltip>
  );
};

function getIconElement(icon: StatusButtonIcon | IconDefinition): IconDefinition {
  switch (icon) {
    case "play":
      return faPlay;
    case "pause":
      return faPause;
    case "check":
      return faCheck;
    default:
      return icon;
  }
}

export default StatusButton;
